package com.vidaensupalabra.vsp.ventanas

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.vidaensupalabra.vsp.ui.theme.*
import com.vidaensupalabra.vsp.datos.ComunidadComentario
import com.vidaensupalabra.vsp.datos.ComunidadPost
import com.vidaensupalabra.vsp.datos.ComunidadRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.coroutines.await
import org.w3c.files.File
import com.vidaensupalabra.vsp.utils.*
import com.vidaensupalabra.vsp.navigation.Router
import com.vidaensupalabra.vsp.navigation.Screen

// --- 1. ESTADOS ---

enum class ComunidadViewState { FEED, DETAIL, CREATE }

// --- UTILIDADES ---

private const val PAGE_SIZE = 10

// --- 2. PANTALLA PRINCIPAL ---

@Composable
fun ComunidadScreen(initialPost: ComunidadPost? = null, initialPostId: String? = null, onBack: () -> Unit) {
    var viewState by remember(initialPost, initialPostId) { mutableStateOf(if (initialPost != null || initialPostId != null) ComunidadViewState.DETAIL else ComunidadViewState.FEED) }
    var selectedPost by remember(initialPost) { mutableStateOf(initialPost) }
    var postToEdit by remember { mutableStateOf<ComunidadPost?>(null) }
    var posts by remember { mutableStateOf<List<ComunidadPost>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var isLoadingMore by remember { mutableStateOf(false) }
    var hasMorePosts by remember { mutableStateOf(true) }
    var currentUser by remember { mutableStateOf<FirebaseUser?>(null) }
    var showBirthdatePrompt by remember { mutableStateOf(false) }
    val coroutineScope = rememberCoroutineScope()

    var fontSize by remember { mutableStateOf(getFromLocalStorage("comunidad_font_size")?.toIntOrNull() ?: 16) }
    val onChangeFontSize: (Int) -> Unit = { delta ->
        val newSize = (fontSize + delta).coerceIn(12, 40)
        if (newSize != fontSize) {
            fontSize = newSize
            saveToLocalStorage("comunidad_font_size", newSize.toString())
        }
    }

    DisposableEffect(Unit) {
        var unsubscribe: dynamic = null
        if (window.asDynamic().addAuthChangeListener != undefined) {
            unsubscribe = window.asDynamic().addAuthChangeListener { user: FirebaseUser? ->
                currentUser = user
            }
        }
        onDispose {
            if (unsubscribe != null && unsubscribe != undefined) {
                try { unsubscribe() } catch (e: Throwable) { console.error("Error unregistering auth listener", e) }
            }
        }
    }

    LaunchedEffect(currentUser) {
        if (currentUser != null) {
            showBirthdatePrompt = ComunidadRepository.checkUserNeedsBirthdate(currentUser!!.uid)
        } else {
            showBirthdatePrompt = false
        }
    }

    LaunchedEffect(viewState) {
        if (viewState == ComunidadViewState.FEED && posts.isEmpty()) {
            isLoading = true
            val fetched = ComunidadRepository.getPosts(limit = PAGE_SIZE)
            posts = fetched
            hasMorePosts = fetched.size >= PAGE_SIZE
            isLoading = false
        }
    }

    LaunchedEffect(initialPostId) {
        if (initialPostId != null && selectedPost == null) {
            isLoading = true
            val fetchedPost = ComunidadRepository.getPostById(initialPostId)
            if (fetchedPost != null) {
                selectedPost = fetchedPost
            } else {
                window.alert("La publicación ya no existe o fue eliminada.")
                viewState = ComunidadViewState.FEED
            }
            isLoading = false
        }
    }

    val handleBack = {
        if (viewState == ComunidadViewState.DETAIL && ((initialPost != null && initialPost.id == selectedPost?.id) || (initialPostId != null && initialPostId == selectedPost?.id))) {
            onBack()
        } else if (viewState == ComunidadViewState.DETAIL || viewState == ComunidadViewState.CREATE) {
            viewState = ComunidadViewState.FEED
        } else {
            onBack()
        }
    }

    val onToggleAmen: (ComunidadPost, Boolean) -> Unit = { post, isAmen ->
        if (currentUser != null) {
            val newAmens = if (isAmen) post.amens + (currentUser!!.uid to true) else post.amens - currentUser!!.uid
            val updatedPost = post.copy(amens = newAmens)
            // Actualizar estado local inmediatamente para evitar retrasos de red visuales
            posts = posts.map { if (it.id == post.id) updatedPost else it }
            if (selectedPost?.id == post.id) {
                selectedPost = updatedPost
            }
            coroutineScope.launch { ComunidadRepository.toggleAmen(post.id, currentUser!!.uid, isAmen) }
        } else {
            window.alert("Inicia sesión para dar Amén")
        }
    }

    val onSharePost: (ComunidadPost) -> Unit = { post ->
        coroutineScope.launch {
            compartirPublicacionNativa(post)
        }
    }

    Div(attrs = {
        style {
            width(100.percent)
            height(100.vh)
            backgroundColor(VspBase)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            fontFamily("sans-serif")
        }
    }) {
        when (viewState) {
            ComunidadViewState.FEED -> ComunidadFeed(
                posts = posts,
                isLoading = isLoading,
                isLoadingMore = isLoadingMore,
                hasMorePosts = hasMorePosts,
                currentUser = currentUser,
                fontSize = fontSize,
                onRefresh = {
                    coroutineScope.launch {
                        isLoading = true
                        val fetched = ComunidadRepository.getPosts(limit = PAGE_SIZE)
                        posts = fetched
                        hasMorePosts = fetched.size >= PAGE_SIZE
                        isLoading = false
                    }
                },
                onPostClick = {
                    selectedPost = it
                    viewState = ComunidadViewState.DETAIL
                },
                onCreateClick = { 
                    postToEdit = null
                    viewState = ComunidadViewState.CREATE 
                },
                onLoadMore = {
                    coroutineScope.launch {
                        if (isLoadingMore || posts.isEmpty()) return@launch
                        isLoadingMore = true
                        val lastTimestamp = posts.last().timestamp
                        val nextPosts = ComunidadRepository.getPosts(limit = PAGE_SIZE, endAtTimestamp = lastTimestamp - 1)
                        if (nextPosts.isNotEmpty()) {
                            val newUniquePosts = nextPosts.filterNot { next -> posts.any { p -> p.id == next.id } }
                            posts = posts + newUniquePosts
                        }
                        hasMorePosts = nextPosts.size >= PAGE_SIZE
                        isLoadingMore = false
                    }
                },
                onToggleAmen = onToggleAmen,
                onSharePost = onSharePost
            )
            ComunidadViewState.DETAIL -> selectedPost?.let { post ->
                ComunidadDetail(
                    post = post,
                    currentUser = currentUser,
                    fontSize = fontSize,
                    onChangeFontSize = onChangeFontSize,
                    onBack = handleBack,
                    onEdit = {
                        postToEdit = it
                        viewState = ComunidadViewState.CREATE
                    },
                    onDelete = {
                        if (window.confirm("¿Seguro que deseas borrar esta publicación?")) {
                            coroutineScope.launch {
                                if (ComunidadRepository.deletePost(it.id)) {
                                    // Optimización: Actualización optimista del estado local
                                    posts = posts.filterNot { p -> p.id == it.id }
                                    if (initialPost != null && initialPost.id == it.id) {
                                        onBack()
                                    } else {
                                        viewState = ComunidadViewState.FEED
                                    }
                                } else {
                                    window.alert("Error al borrar el post.")
                                }
                            }
                        }
                    },
                    onToggleAmen = { isAmen -> onToggleAmen(post, isAmen) },
                    onShare = { onSharePost(post) },
                    onAddComentario = { texto ->
                        coroutineScope.launch {
                            if (currentUser == null) return@launch
                            val timestamp = Clock.System.now().toEpochMilliseconds()
                            val fechaDisplay = getCurrentFormattedDate()
                            
                            val comentario = ComunidadComentario(
                                userId = currentUser!!.uid, autor = currentUser!!.name, autorFoto = currentUser!!.photo,
                                texto = texto, timestamp = timestamp, fecha = fechaDisplay,
                                esAdmin = isAdmin(currentUser)
                            )
                            val commentId = "${currentUser!!.uid}_$timestamp"
                            
                            val newComentarios = post.comentarios + (commentId to comentario)
                            val updatedPost = post.copy(comentarios = newComentarios)
                            posts = posts.map { if (it.id == post.id) updatedPost else it }
                            selectedPost = updatedPost
                            
                            ComunidadRepository.addComentario(post.id, commentId, comentario)
                        }
                    },
                    onDeleteComentario = { commentId ->
                        if (window.confirm("¿Seguro que deseas borrar este comentario?")) {
                            coroutineScope.launch {
                                if (ComunidadRepository.deleteComentario(post.id, commentId)) {
                                    val newComentarios = post.comentarios - commentId
                                    val updatedPost = post.copy(comentarios = newComentarios)
                                    posts = posts.map { if (it.id == post.id) updatedPost else it }
                                    selectedPost = updatedPost
                                } else {
                                    window.alert("Error al borrar el comentario.")
                                }
                            }
                        }
                    }
                )
            }
            ComunidadViewState.CREATE -> ComunidadCreate(
                postToEdit = postToEdit,
                onBack = handleBack,
                onSubmit = { titulo, texto, youtubeUrl, imagenUrl, linkUrl, linkTitle, linkDesc, linkImg ->
                    coroutineScope.launch {
                        if (currentUser == null) return@launch
                        val timestampNow = Clock.System.now().toEpochMilliseconds()
                        val fechaDisplay = getCurrentFormattedDate()
                        
                        val nuevoPost = ComunidadPost(
                            id = postToEdit?.id ?: "",
                            userId = currentUser!!.uid,
                            autor = currentUser!!.name,
                            autorFoto = currentUser!!.photo,
                            timestamp = postToEdit?.timestamp ?: timestampNow,
                            fecha = postToEdit?.fecha ?: fechaDisplay,
                            titulo = titulo,
                            contenido = texto,
                            youtubeVideoId = youtubeUrl.takeIf { it.isNotBlank() },
                            imagenUrl = imagenUrl ?: postToEdit?.imagenUrl,
                            linkPreviewUrl = linkUrl,
                            linkPreviewTitle = linkTitle,
                            linkPreviewDesc = linkDesc,
                            linkPreviewImage = linkImg,
                            amens = postToEdit?.amens ?: emptyMap(),
                            comentarios = postToEdit?.comentarios ?: emptyMap(),
                            esAdmin = postToEdit?.esAdmin ?: isAdmin(currentUser)
                        )
                        
                        val exito = if (postToEdit == null) ComunidadRepository.createPost(nuevoPost) else ComunidadRepository.updatePost(nuevoPost)
                        if (exito) {
                            if (postToEdit != null) {
                                // Optimización: Edición local en memoria sin re-descargar el historial
                                posts = posts.map { p -> if (p.id == nuevoPost.id) nuevoPost else p }
                                if (selectedPost?.id == nuevoPost.id) selectedPost = nuevoPost
                            } else {
                                // Al crear, recargamos solo la primera página para obtener el ID real de Firebase
                                val fetched = ComunidadRepository.getPosts(limit = PAGE_SIZE)
                                posts = fetched
                                hasMorePosts = fetched.size >= PAGE_SIZE
                            }
                            viewState = ComunidadViewState.FEED
                        } else {
                            window.alert("Error al conectar con el servidor. Intenta de nuevo.")
                        }
                    }
                }
            )
        }

        if (showBirthdatePrompt && currentUser != null) {
            BirthdateDialog(
                onSubmit = { date ->
                    coroutineScope.launch {
                        if (ComunidadRepository.saveUserBirthdate(currentUser!!.uid, date)) {
                            showBirthdatePrompt = false
                        } else {
                            window.alert("Error al guardar la fecha de nacimiento. Intenta de nuevo.")
                        }
                    }
                }
            )
        }
    }
}

// --- 3. VISTA: EL FEED O MURO ---

@Composable
private fun ComunidadFeed(
    posts: List<ComunidadPost>,
    isLoading: Boolean,
    isLoadingMore: Boolean,
    hasMorePosts: Boolean,
    currentUser: FirebaseUser?,
    fontSize: Int,
    onRefresh: () -> Unit,
    onPostClick: (ComunidadPost) -> Unit,
    onCreateClick: () -> Unit,
    onLoadMore: () -> Unit,
    onToggleAmen: (ComunidadPost, Boolean) -> Unit,
    onSharePost: (ComunidadPost) -> Unit
) {
    var searchQuery by remember { mutableStateOf("") }
    val filteredPosts = remember(posts, searchQuery) {
        if (searchQuery.isBlank()) posts
        else posts.filter { 
            it.titulo.contains(searchQuery, ignoreCase = true) || 
            it.autor.contains(searchQuery, ignoreCase = true) 
        }
    }

    // Header
    Div(attrs = {
        style {
            width(100.percent); padding(16.px); boxSizing("border-box")
            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(16.px)
            backgroundColor(VspMarcoTransparente50); property("border-bottom", "1px solid rgba(255,255,255,0.1)")
        }
    }) {
        Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); width(100.percent) } }) {
            H2(attrs = { style { color(White); margin(0.px); fontSize(24.px) } }) { Text("Comunidad") }
            
            Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(12.px) } }) {
                Button(attrs = {
                    onClick { onRefresh() }
                    style { backgroundColor(Color("transparent")); color(White); border(0.px); fontSize(20.px); cursor("pointer"); padding(4.px) }
                }) { Text("🔄") }
    
                if (currentUser != null) {
                    if (currentUser.photo != null) {
                        Img(src = currentUser.photo!!, attrs = { 
                            onClick { it.nativeEvent.stopPropagation(); Router.navigateTo(Screen.Perfil()) }
                            style { width(36.px); height(36.px); borderRadius(50.percent); cursor("pointer") } 
                        })
                    }
                    Button(attrs = {
                        onClick { onCreateClick() }
                        style { backgroundColor(VspMarco); color(White); border(0.px); borderRadius(8.px); padding(8.px, 16.px); fontWeight("bold"); cursor("pointer") }
                    }) { Text("+ Compartir") }
                } else {
                    Button(attrs = {
                        onClick { window.asDynamic().signInWithGoogle() }
                        style { backgroundColor(White); color(Color("black")); border(0.px); borderRadius(8.px); padding(8.px, 16.px); fontWeight("bold"); cursor("pointer") }
                    }) { Text("🔑 Ingresar") }
                }
            }
        }
        
        Input(type = InputType.Text, attrs = {
            value(searchQuery)
            onInput { searchQuery = it.value }
            attr("placeholder", "🔍 Buscar por título o autor...")
            style {
                width(100.percent); padding(10.px, 16.px); borderRadius(8.px)
                border(1.px, LineStyle.Solid, rgba(255,255,255,0.2)); backgroundColor(rgba(0, 0, 0, 0.2))
                color(White); fontSize(16.px); boxSizing("border-box"); outline("none")
            }
        })
    }

    // Lista de Posts
    Div(attrs = {
        style {
            width(100.percent); maxWidth(800.px)
            flex(1); property("overflow-y", "auto")
            padding(16.px); boxSizing("border-box")
            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(16.px)
        }
    }) {
        if (isLoading) {
            Div(attrs = { style { color(VspMarco); padding(24.px); textAlign("center"); fontWeight("bold") } }) {
                Text("Cargando publicaciones...")
            }
        } else if (posts.isEmpty()) {
            P(attrs = { style { color(White); padding(24.px); textAlign("center") } }) {
                Text("No hay publicaciones aún. ¡Sé el primero en compartir algo!")
            }
        } else {
            if (filteredPosts.isEmpty()) {
                P(attrs = { style { color(White); padding(24.px); textAlign("center") } }) {
                    Text("No se encontraron resultados para '${searchQuery}'.")
                }
            } else {
                filteredPosts.forEach { post ->
                    key(post.id) {
                        PostCard(post = post, currentUser = currentUser, fontSize = fontSize, onClick = { onPostClick(post) }, onToggleAmen = { isAmen -> onToggleAmen(post, isAmen) }, onShare = {
                            onSharePost(post)
                        })
                    }
                }
            }
            if (hasMorePosts && posts.isNotEmpty() && searchQuery.isBlank()) {
                Button(attrs = {
                    onClick { onLoadMore() }
                    style {
                        width(100.percent); padding(14.px); marginTop(8.px); marginBottom(24.px)
                        backgroundColor(VspMarcoTransparente50); color(White); border(0.px)
                        borderRadius(8.px); cursor("pointer"); fontWeight("bold"); fontSize(16.px)
                    }
                }) {
                    Text(if (isLoadingMore) "Cargando más..." else "Cargar más publicaciones")
                }
            }
        }
    }
}

// --- 4. VISTA: DETALLE DEL POST ---

@Composable
private fun ComunidadDetail(
    post: ComunidadPost, 
    currentUser: FirebaseUser?,
    fontSize: Int,
    onChangeFontSize: (Int) -> Unit,
    onBack: () -> Unit,
    onEdit: (ComunidadPost) -> Unit,
    onDelete: (ComunidadPost) -> Unit,
    onToggleAmen: (Boolean) -> Unit,
    onShare: () -> Unit,
        onAddComentario: (String) -> Unit,
        onDeleteComentario: (String) -> Unit
) {
    val isOwner = remember(currentUser, post.userId) { currentUser != null && post.userId == currentUser.uid }
    val canDeletePost = remember(isOwner, currentUser) { isOwner || isAdmin(currentUser) }
    var expandedInlineImage by remember { mutableStateOf<String?>(null) }

    Div(attrs = {
        style { width(100.percent); padding(16.px); boxSizing("border-box"); backgroundColor(VspMarcoTransparente50); display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center) }
    }) {
        Span(attrs = {
            onClick { onBack() }
            style { color(White); cursor("pointer"); fontSize(24.px) }
        }) { Text("⬅️") }
        
        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center) } }) {
            Button(attrs = { onClick { onChangeFontSize(-2) }; style { backgroundColor(Color("transparent")); color(White); border(0.px); fontWeight("bold"); fontSize(14.px); cursor("pointer") } }) { Text("A-") }
            Button(attrs = { onClick { onChangeFontSize(2) }; style { backgroundColor(Color("transparent")); color(White); border(0.px); fontWeight("bold"); fontSize(18.px); cursor("pointer") } }) { Text("A+") }
        }

        if (canDeletePost) {
            Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px) } }) {
                if (isOwner) {
                    Span(attrs = { onClick { onEdit(post) }; style { color(VspMarco); cursor("pointer"); fontSize(20.px) } }) { Text("✏️") }
                }
                if (canDeletePost) {
                    Span(attrs = { onClick { onDelete(post) }; style { color(Color("red")); cursor("pointer"); fontSize(20.px) } }) { Text("🗑️") }
                }
            }
        }
    }

    Div(attrs = {
        style {
            width(100.percent); maxWidth(800.px)
            flex(1); property("overflow-y", "auto")
            padding(24.px); boxSizing("border-box")
        }
    }) {
        H1(attrs = { style { color(VspMarco); margin(0.px, 0.px, 8.px, 0.px) } }) { Text(post.titulo) }
        P(attrs = { style { color(rgba(255, 255, 255, 0.7)); fontSize(14.px); marginTop(0.px) } }) {
            Text("Por ")
            Span(attrs = { 
                onClick { Router.navigateTo(Screen.Perfil(post.userId, post.autor, post.autorFoto)) }
                style { fontWeight("bold"); color(White); cursor("pointer") } 
            }) { Text(post.autor) }
            AdminBadge(post.esAdmin)
            Text(" • ${post.fecha}")
        }
        
        if (post.youtubeVideoId != null) {
            val tipo = identificarTipoMultimedia(post.youtubeVideoId)
            when (tipo) {
                TipoMultimedia.YOUTUBE -> {
                    Div(attrs = { style { width(100.percent); property("aspect-ratio", "16 / 9"); borderRadius(12.px); property("overflow", "hidden"); marginBottom(16.px) } }) {
                        YouTubeVideoPlayer(videoId = post.youtubeVideoId)
                    }
                }
                TipoMultimedia.NATIVO_MP4 -> {
                    Video(attrs = {
                        attr("controls", "true"); attr("playsinline", "true")
                        style { width(100.percent); borderRadius(12.px); marginBottom(16.px); property("max-height", "400px"); property("object-fit", "contain"); backgroundColor(Color.black) }
                    }) {
                        Source(attrs = { attr("src", post.youtubeVideoId); attr("type", "video/mp4") })
                    }
                }
                TipoMultimedia.INSTAGRAM -> {
                    val igId = extraerIdInstagram(post.youtubeVideoId)
                    if (igId != null) {
                        Iframe(attrs = {
                            attr("src", "https://www.instagram.com/p/$igId/embed/")
                            attr("scrolling", "no")
                            attr("allowtransparency", "true")
                            attr("allow", "encrypted-media; picture-in-picture")
                            style { 
                                width(100.percent); maxWidth(400.px); height(600.px); border { width = 0.px }
                                borderRadius(12.px); marginBottom(16.px); property("margin", "0 auto"); display(DisplayStyle.Block)
                            }
                        })
                    } else {
                        LinkPreviewCard(url = post.youtubeVideoId, title = "Ver en Instagram", desc = "Haz clic para ver el contenido", imageUrl = null)
                    }
                }
                TipoMultimedia.DESCONOCIDO -> {
                    LinkPreviewCard(url = post.youtubeVideoId, title = "Enlace Adjunto", desc = "Haz clic para ver el contenido", imageUrl = null)
                }
            }
        }
        
        if (post.imagenUrl != null) {
            PostImage(post.imagenUrl)
        }
        
        if (post.linkPreviewUrl != null && post.linkPreviewTitle != null) {
            LinkPreviewCard(post.linkPreviewUrl, post.linkPreviewTitle, post.linkPreviewDesc ?: "", post.linkPreviewImage)
        }
        
        key(post.contenido) {
            Div(attrs = { 
                onClick { event ->
                    val target = event.nativeEvent.target.asDynamic()
                    if (target.tagName == "IMG") {
                        expandedInlineImage = target.src as String
                        // Prevenimos que el click se propague y dispare otros eventos
                        event.nativeEvent.stopPropagation() 
                    }
                }
                style { color(White); fontSize(fontSize.px); lineHeight("1.6"); property("white-space", "pre-wrap") } 
                ref {
                    it.innerHTML = parsearMarkdownBasico(post.contenido)
                    onDispose {}
                }
            })
        }

        // Interacciones y Comentarios
        Div(attrs = { style { marginTop(32.px); property("border-top", "1px solid rgba(255,255,255,0.2)"); paddingTop(16.px); display(DisplayStyle.Flex); alignItems(AlignItems.Center) } }) {
            val hasAmen = currentUser != null && post.amens.containsKey(currentUser.uid)
            Span(attrs = {
                onClick { onToggleAmen(!hasAmen) }
                style { color(if (hasAmen) VspMarco else White); fontWeight("bold"); cursor("pointer"); fontSize(18.px) }
            }) { Text("👏 Amén (${post.amens.size})") }
            
            Span(attrs = {
                onClick { onShare() }
                style { color(White); fontWeight("bold"); cursor("pointer"); fontSize(18.px); property("margin-left", "auto") }
            }) { Text("📤 Compartir") }
        }
        
        Div(attrs = { style { marginTop(32.px) } }) {
            H3(attrs = { style { color(White); marginBottom(16.px) } }) { Text("Comentarios (${post.comentarios.size})") }
            
            val sortedComments = remember(post.comentarios) { post.comentarios.entries.sortedBy { it.value.timestamp } }
            Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(12.px) } }) {
                sortedComments.forEach { (commentId, comentario) ->
                    key(commentId) {
                        val isOwnComment = currentUser?.uid == comentario.userId
                        Div(attrs = { style { backgroundColor(rgba(0,0,0,0.2)); padding(12.px); borderRadius(8.px) } }) {
                            Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); marginBottom(8.px) } }) {
                                val onCommentProfileClick = { Router.navigateTo(Screen.Perfil(comentario.userId, comentario.autor, comentario.autorFoto)) }
                                if (comentario.autorFoto != null) {
                                    Img(src = comentario.autorFoto, attrs = { onClick { onCommentProfileClick() }; style { width(20.px); height(20.px); borderRadius(50.percent); marginRight(8.px); cursor("pointer") } })
                                }
                                Span(attrs = { onClick { onCommentProfileClick() }; style { color(White); fontWeight("bold"); fontSize(13.px); flex(1); cursor("pointer") } }) { Text(comentario.autor) }
                                AdminBadge(comentario.esAdmin)
                                Span(attrs = { style { color(rgba(255, 255, 255, 0.5)); fontSize(11.px) } }) { Text(comentario.fecha) }
                                
                                if (isOwnComment || canDeletePost) {
                                    Span(attrs = { 
                                        onClick { onDeleteComentario(commentId) }
                                        style { color(Color("red")); cursor("pointer"); fontSize(12.px); marginLeft(8.px) } 
                                    }) { Text("🗑️") }
                                }
                            }
                            P(attrs = { style { color(White); fontSize(14.px); margin(0.px); lineHeight("1.4") } }) {
                                Text(comentario.texto)
                            }
                        }
                    }

        if (expandedInlineImage != null) {
            Div(attrs = {
                onClick { expandedInlineImage = null }
                style {
                    position(Position.Fixed); top(0.px); left(0.px); width(100.vw); height(100.vh)
                    backgroundColor(rgba(0, 0, 0, 0.9)); property("z-index", 2000)
                    display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); alignItems(AlignItems.Center)
                    cursor("zoom-out")
                }
            }) {
                Img(src = expandedInlineImage!!, alt = "Imagen ampliada", attrs = {
                    style { maxWidth(95.percent); maxHeight(95.percent); property("object-fit", "contain") }
                })
            }
        }
                }
            }
            
            if (currentUser != null) {
                var nuevoComentario by remember { mutableStateOf("") }
                Div(attrs = { style { marginTop(16.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(8.px) } }) {
                    TextArea(value = nuevoComentario, attrs = {
                        onInput { nuevoComentario = it.value }
                        attr("placeholder", "Escribe un comentario de aliento...")
                        style { width(100.percent); minHeight(60.px); padding(12.px); borderRadius(8.px); border(1.px, LineStyle.Solid, VspMarco); backgroundColor(VspMarcoTransparente50); color(White); fontFamily("sans-serif"); fontSize(14.px); boxSizing("border-box"); property("resize", "vertical") }
                    })
                    Button(attrs = {
                        onClick {
                            if (nuevoComentario.isNotBlank()) { onAddComentario(nuevoComentario); nuevoComentario = "" }
                        }
                        style { padding(8.px, 16.px); backgroundColor(VspMarco); color(White); fontWeight("bold"); border(0.px); borderRadius(8.px); cursor("pointer"); alignSelf(AlignSelf.FlexEnd) }
                    }) { Text("Comentar") }
                }
            } else {
                P(attrs = { style { color(VspMarco); textAlign("center"); marginTop(16.px) } }) { Text("Debes iniciar sesión para comentar.") }
            }
        }
    }
}

@Composable
private fun ComunidadCreate(
    postToEdit: ComunidadPost?,
    onBack: () -> Unit, 
    onSubmit: (String, String, String, String?, String?, String?, String?, String?) -> Unit
) {
    var titulo by remember(postToEdit) { mutableStateOf(postToEdit?.titulo ?: "") }
    var contenido by remember(postToEdit) { mutableStateOf(postToEdit?.contenido ?: "") }
    var mediaUrl by remember(postToEdit) { mutableStateOf(postToEdit?.youtubeVideoId?.let { 
        if (identificarTipoMultimedia(it) == TipoMultimedia.YOUTUBE) "https://youtu.be/$it" else it 
    } ?: "") }
    var fileToUpload by remember { mutableStateOf<File?>(null) }
    var isSubmitting by remember { mutableStateOf(false) }
    var isUploadingInline by remember { mutableStateOf(false) }
    
    var linkPreviewUrl by remember(postToEdit) { mutableStateOf(postToEdit?.linkPreviewUrl) }
    var linkPreviewTitle by remember(postToEdit) { mutableStateOf(postToEdit?.linkPreviewTitle) }
    var linkPreviewDesc by remember(postToEdit) { mutableStateOf(postToEdit?.linkPreviewDesc) }
    var linkPreviewImage by remember(postToEdit) { mutableStateOf(postToEdit?.linkPreviewImage) }
    var isFetchingPreview by remember { mutableStateOf(false) }
    
    var activeFormats by remember { mutableStateOf(setOf<String>()) }
    val checkFormats: () -> Unit = {
        val doc = window.document.asDynamic()
        val newFormats = mutableSetOf<String>()
        if (doc.queryCommandState("bold") as Boolean) newFormats.add("bold")
        if (doc.queryCommandState("italic") as Boolean) newFormats.add("italic")
        val block = doc.queryCommandValue("formatBlock") as? String ?: ""
        if (block.contains("blockquote", ignoreCase = true)) newFormats.add("blockquote")
        activeFormats = newFormats
    }

    val coroutineScope = rememberCoroutineScope()

    LaunchedEffect(contenido) {
        val url = AUTO_LINK_REGEX.find(contenido)?.groupValues?.get(2)
        if (url != null && url != linkPreviewUrl && linkPreviewUrl != "ignore" && !isFetchingPreview) {
            isFetchingPreview = true
            try {
                val response = window.fetch("https://api.codetabs.com/v1/proxy/?quest=$url").await()
                if (response.ok as Boolean) {
                    val responseText = response.text().await()
                    val doc = js("new DOMParser()").parseFromString(responseText, "text/html")
                    
                    val getMeta = { property: String ->
                        val node = doc.querySelector("meta[property='$property']") ?: doc.querySelector("meta[name='$property']")
                        node?.getAttribute("content") as? String ?: ""
                    }
                    
                    val title = getMeta("og:title").takeIf { it.isNotBlank() } ?: (doc.title as? String) ?: ""
                    if (title.isNotBlank()) {
                        linkPreviewUrl = url
                        linkPreviewTitle = title
                        linkPreviewDesc = getMeta("og:description").takeIf { it.isNotBlank() } ?: getMeta("description")
                        linkPreviewImage = getMeta("og:image").takeIf { it.isNotBlank() }
                    }
                }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                console.log("No preview for $url")
            } finally {
                isFetchingPreview = false
            }
        } else if (url == null && linkPreviewUrl != null) {
            linkPreviewUrl = null; linkPreviewTitle = null; linkPreviewDesc = null; linkPreviewImage = null
        }
    }

    Div(attrs = {
        style { width(100.percent); padding(16.px); boxSizing("border-box"); backgroundColor(VspMarcoTransparente50) }
    }) {
        Span(attrs = {
            onClick { onBack() }
            style { color(White); cursor("pointer"); fontSize(24.px) }
        }) { Text("⬅️") }
    }

    Div(attrs = {
        style {
            width(100.percent); maxWidth(600.px)
            flex(1); property("overflow-y", "auto")
            padding(24.px); boxSizing("border-box")
            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(16.px)
        }
    }) {
        H2(attrs = { style { color(White); margin(0.px) } }) { Text(if (postToEdit != null) "Editar Publicación" else "Crear Nueva Publicación") }

        FormInput(label = "Título", value = titulo, onValueChange = { titulo = it }, placeholder = "Ej. Pensamiento del día")
        
        // Input oculto para manejar la subida de imágenes en línea dentro del WYSIWYG
        Input(type = InputType.File, attrs = {
            id("hidden-inline-image-input")
            style { display(DisplayStyle.None) }
            attr("accept", "image/*")
            onChange { event ->
                val file = event.target.files?.item(0) ?: return@onChange
                coroutineScope.launch {
                    isUploadingInline = true
                    try {
                        val promise = window.asDynamic().subirImagenFirebase(file) as kotlin.js.Promise<String?>
                        val uploadedUrl = promise.await()
                        if (uploadedUrl != null) {
                            val selection = window.asDynamic().getSelection()
                            val savedRange = window.asDynamic().savedRange
                            if (selection != null && savedRange != null) {
                                selection.removeAllRanges()
                                selection.addRange(savedRange)
                            }
                            aplicarFormatoVisual("insertImage", uploadedUrl)
                            val editor = window.document.getElementById("post-content-area").asDynamic()
                            contenido = htmlToMarkdown(editor.innerHTML as String)
                        }
                    } catch (e: CancellationException) {
                        throw e
                    } catch (e: Exception) {
                        console.error("Error crítico subiendo imagen inline a Firebase:", e)
                        window.alert("Error subiendo la imagen. Revisa tu conexión o permisos.")
                    } finally {
                        isUploadingInline = false
                        window.asDynamic().savedRange = null
                    }
                }
            }
        })
        
        // Contenido (WYSIWYG)
        Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(4.px) } }) {
            // Hack para inyectar CSS del placeholder de contenteditable
            Div(attrs = { 
                ref { 
                    it.innerHTML = "<style>#post-content-area[contenteditable]:empty::before { content: attr(placeholder); color: rgba(255, 255, 255, 0.5); pointer-events: none; display: block; }</style>" 
                    onDispose {} 
                }
                style { display(DisplayStyle.None) }
            })

            Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center) } }) {
                Label(attrs = { style { color(White); fontSize(14.px) } }) { Text("Contenido o Reflexión") }
                Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px) } }) {
                    val btnStyle: (Boolean) -> StyleScope.() -> Unit = { isActive -> { backgroundColor(if (isActive) rgba(255, 255, 255, 0.2) else Color("transparent")); color(VspMarco); border(0.px); borderRadius(6.px); cursor("pointer"); fontSize(16.px); fontWeight("bold"); padding(4.px, 8.px) } }
                    Button(attrs = { onClick { aplicarFormatoVisual("bold"); checkFormats() }; style { btnStyle(activeFormats.contains("bold"))(this) }; title("Negrita (WhatsApp: *texto*)") }) { Text("B") }
                    Button(attrs = { onClick { aplicarFormatoVisual("italic"); checkFormats() }; style { btnStyle(activeFormats.contains("italic"))(this) }; title("Cursiva (WhatsApp: _texto_)") }) { Text("I") }
                    Button(attrs = { 
                        onClick { 
                            val url = window.prompt("Ingresa el enlace:", "https://")
                            if (!url.isNullOrBlank()) { aplicarFormatoVisual("createLink", url); checkFormats() }
                        }
                        style { btnStyle(false)(this) }; title("Enlace") 
                    }) { Text("🔗") }
                    Button(attrs = { onClick { aplicarFormatoVisual("formatBlock", "blockquote"); checkFormats() }; style { btnStyle(activeFormats.contains("blockquote"))(this) }; title("Cita") }) { Text("❝") }
                    Button(attrs = { 
                        onClick { 
                            val selection = window.asDynamic().getSelection()
                            if (selection != null && selection.rangeCount > 0) window.asDynamic().savedRange = selection.getRangeAt(0)
                            window.document.getElementById("hidden-inline-image-input")?.asDynamic()?.click() 
                        }
                    style { btnStyle(false)(this) }; title("Insertar Imagen en el texto") 
                }) { Text("🖼️") }
                }
            }
            Div(
                attrs = {
                    id("post-content-area")
                    attr("contenteditable", "true")
                    attr("placeholder", "Escribe tu publicación aquí...")
                addEventListener("input") { event ->
                    val target = event.nativeEvent.target.asDynamic()
                    val htmlContent = target.innerHTML as String
                    contenido = htmlToMarkdown(htmlContent)
                    checkFormats()
                }
                addEventListener("keyup") { checkFormats() }
                addEventListener("mouseup") { checkFormats() }
                    style {
                        width(100.percent); minHeight(150.px); padding(16.px); borderRadius(12.px)
                        border(1.px, LineStyle.Solid, VspMarco); backgroundColor(VspMarcoTransparente50)
                        color(White); fontFamily("sans-serif"); fontSize(16.px); boxSizing("border-box")
                        property("resize", "vertical"); outline("none"); property("overflow-y", "auto")
                    }
                }
            )

            LaunchedEffect(contenido) {
                val element = window.document.getElementById("post-content-area").asDynamic()
                if (element != null) {
                    val currentHtml = element.innerHTML as? String ?: ""
                    val currentMarkdown = htmlToMarkdown(currentHtml)
                    if (currentMarkdown != contenido) {
                        element.innerHTML = parsearMarkdownBasico(contenido)
                    }
                }
            }
        }
        
        if (linkPreviewUrl != null && linkPreviewTitle != null && linkPreviewUrl != "ignore") {
            LinkPreviewCard(linkPreviewUrl!!, linkPreviewTitle!!, linkPreviewDesc ?: "", linkPreviewImage) {
                linkPreviewUrl = "ignore"
                linkPreviewTitle = null
                linkPreviewDesc = null
                linkPreviewImage = null
            }
        }

        FormInput(label = "Enlace de Video o Red Social (Opcional)", value = mediaUrl, onValueChange = { mediaUrl = it }, placeholder = "YouTube, MP4, Instagram...")
        
        // Input File
        Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(4.px) } }) {
            Label(attrs = { style { color(White); fontSize(14.px) } }) { Text("Subir Imagen (Opcional)") }
            Input(type = InputType.File, attrs = {
                attr("accept", "image/*")
                onChange { event -> 
                    val files = event.target.files
                    fileToUpload = if (files != null && files.length > 0) files.item(0) else null
                }
                style { color(White); padding(8.px, 0.px) }
            })
        }

        Button(attrs = {
            onClick { 
                if (isSubmitting) return@onClick
                if (titulo.isNotBlank() && contenido.isNotBlank()) {
                    coroutineScope.launch {
                        isSubmitting = true
                        try {
                            var uploadedUrl: String? = null
                            if (fileToUpload != null) {
                                try {
                                val promise = window.asDynamic().subirImagenFirebase(fileToUpload) as kotlin.js.Promise<String?>
                                uploadedUrl = promise.await()
                                } catch (e: CancellationException) {
                                    throw e
                                } catch (e: Exception) {
                                    console.error("Error crítico subiendo imagen de post a Firebase:", e)
                                    window.alert("Error subiendo la imagen. Revisa tu conexión o permisos.")
                                    return@launch // Evita publicar el post si falla la imagen
                                }
                            }
                            val mediaUrlLimpiada = extraerMediaUrl(mediaUrl)
                            onSubmit(titulo, contenido, mediaUrlLimpiada, uploadedUrl, linkPreviewUrl.takeIf { it != "ignore" }, linkPreviewTitle, linkPreviewDesc, linkPreviewImage)
                        } finally {
                            isSubmitting = false // Previene el bloqueo permanente del botón
                        }
                    }
                } else {
                    window.alert("Por favor completa el título y el contenido.")
                }
            }
            style {
                width(100.percent); padding(14.px); marginTop(16.px)
                backgroundColor(if (isSubmitting) Color("gray") else VspMarco); color(White); fontWeight("bold"); fontSize(16.px)
                border(0.px); borderRadius(8.px); cursor(if (isSubmitting) "not-allowed" else "pointer")
            }
        }) { Text(if (isSubmitting) "Guardando..." else if (postToEdit != null) "Guardar Cambios" else "Publicar en la Comunidad") }

        if (isSubmitting || isUploadingInline) {
            FullScreenLoader(if (isSubmitting) "Publicando..." else "Subiendo imagen al servidor...")
        }
    }
}